import { Activity } from '../entities/shared/activity';
import { ActivityEntityName } from '../entities/shared/activity.entity.name';
import { ActivityType } from '../entities/shared/activity.type';
import { Bill, Invoice, InvoiceBillingCompany, InvoiceLine, InvoiceType, InvoicingSummary, LineAccounting } from '../entities/invoicing/invoicing';
import { InvoiceStatus } from '../entities/invoicing/invoicing.enum';
import { InvoiceContact } from '../entities/invoicing/invoicing.contact';
import { CodeFinance, CodeInvoiceStatus, CodeStatus, EmbeddedCode, EmbeddedCodeGroup } from '../entities/shared/code';
import { Company } from '../entities/tenant/company';
import { Contact } from '../entities/contact/contact';
import { CreditAccountType, CreditAmount, CreditOptions, DueDateType } from '../entities/contact/contact.options';
import { ConvertToInvoiceRequest } from '../entities/invoicing/invoicing.request';
import { Entity, EntityLink, ItemLinkEntity } from '../entities/entity';
import { InvoiceCalculationDomain } from '../entities/invoicing/invoice.calculation.domain';
import { inclusiveLineTax } from '../entities/invoicing/inclusive.line.tax';
import { InvoiceCreatedType, InvoicingViewByDays } from '../entities/invoicing/invoicing.enum';
import { InvoiceViewSummary } from '../entities/invoicing/invoicing.view';
import { Job } from '../entities/job/job';
import { LabourCodeId, PrepaymentSetting } from '../entities/settings/general.settings';
import { PurchaseOrderLine, TaxInclusiveOption } from '../entities/purchase/purchase.order.line';
import { SitePro } from '../entities/crm/sites/site.pro';
import { TaxRate } from '../entities/taxrate/tax.rate';
import { TimesheetTable } from '../entities/time.billing/timesheet.entry';
import { DateUtility } from './date.utility';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import { LinkRelationship } from '../enums/link.relationship';
import * as Enumerable from 'linq';

export class InvoiceUtility {


    public static getManualInvoice(request: ConvertToInvoiceRequest): Invoice {
        request.invoiceType = InvoiceType.Invoice;
        request.createdType = InvoiceCreatedType.Default;
        const invoice = this.get<Invoice>(request);
        return invoice;
    }

    public static getInvoiceFromJob(request: ConvertToInvoiceRequest): Invoice {
        request.invoiceType = InvoiceType.Invoice;
        request.createdType = InvoiceCreatedType.FromJob;
        const invoice = this.get<Invoice>(request);
        return invoice;
    }

    public static getInvoiceFromWorkOrder(request: ConvertToInvoiceRequest): Invoice {
        request.invoiceType = InvoiceType.Invoice;
        request.createdType = InvoiceCreatedType.FromWorkOrder;
        const invoice = this.get<Invoice>(request);
        return invoice;
    }

    public static getBill(request: ConvertToInvoiceRequest, isPrepayment: boolean, settings: PrepaymentSetting): Bill {
        if (isPrepayment) {
            request.invoiceType = InvoiceType.PrepaymentSupplier;
        }
        else {
            request.invoiceType = InvoiceType.Bill;
        }

        request = { ...request, company: request.company };
        const bill = this.get<Bill>(request);

        if (isPrepayment) {
            return this.buildBillLinePrepayment(bill, request.taxRate, settings);
        }

        return bill;
    }

    private static buildBillLinePrepayment(bill: Bill, taxRate: TaxRate, settings: PrepaymentSetting): Bill {
        const line: InvoiceLine = {
            _id: undefined,
            name: settings?.name ?? 'Prepayment',
            description: null,
            quantity: 1,
            price: 0,
            subTotal: 0,
            tax: 0,
            total: 0,
            taxRate,
            codeInvoicingStatus: CodeInvoiceStatus.Billable,
        };

        bill.lines = [line];
        return bill;
    }

    public static getInvoiceContact(customer: Contact, site: SitePro): InvoiceContact {
        const { billTo } = customer;
        const credit = billTo?.company?._id !== customer?._id
            ? billTo?.company?.credit
            : customer?.credit;

        const contact = customer ?
            {
                _id: billTo?.company?._id,
                // The board Customer column reads name. Bill To often has a trading name and no legalName (WBS-11813).
                name: billTo?.company?.legalName || billTo?.company?.name || customer?.name,
                friendlyName: billTo?.company?.name,
                address: billTo?.site?.address,
                email: billTo?.contact?.email,
                phone: billTo?.contact?.mobile,
                contactId: billTo?.contact?._id,
                contactName: billTo?.contact?.name,
                originalAccount: {
                    _id: customer?._id,
                    name: customer?.name
                },
                type: 'Customer',
                invoiceTheme: site?.invoiceTheme ?? customer?.invoiceTheme ?? null,
                credit: credit,
            } as InvoiceContact
            : null;

        return contact;
    }

    public static getBillContact(technician: Contact): InvoiceContact {
        const contact = technician ?
            {
                _id: technician?.billTo?.company?._id,
                name: technician?.billTo?.company?.legalName,
                address: technician.billTo?.site?.address,
                email: technician.billTo?.contact?.email,
                phone: technician.billTo?.contact?.mobile,
                contactId: technician.billTo?.contact?._id,
                contactName: technician.billTo?.contact?.name,
                friendlyName: technician.billTo?.company?.name,
                originalAccount: {
                    _id: technician._id,
                    name: technician.name
                },
                type: 'Technician',
                billTheme: technician?.billTheme
            } as InvoiceContact
            : null;

        return contact;
    }

    private static get<T extends Bill | Invoice>(request: ConvertToInvoiceRequest): T {

        const { job, workOrder, purchaseOrder, company, taxRate, customer, technician, invoiceType: invoicingType, createdType, codesFinance, labourCode, defaultIncomeTaxRate, site } = request;

        let contact: InvoiceContact = null;
        let lines: InvoiceLine[] = [];
        let links: EntityLink[] = [];
        let taxInclusiveOption = TaxInclusiveOption.Exclusive;

        let timezone = job?.site?.address?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
        const jobAddress = job?.site?.address;

        let jobEntity: Entity = job ? { _id: job._id, name: job.jobNumber } : null;
        const workOrderEntity: Entity = workOrder?._id ? { _id: workOrder._id, name: workOrder.workOrderNumber } : null;

        const billingCompany = InvoiceUtility.buildInvoiceCompany(company);


        switch (invoicingType) {

            case InvoiceType.Bill: {

                contact = this.getBillContact(technician);

                switch (createdType) {

                    case InvoiceCreatedType.FromWorkOrder: {
                        lines = Enumerable
                            .from(workOrder?.codes)
                            .where(x => InvoiceUtility.validBillCode(x, request?.suppliedByContractor))
                            .select(code => {

                                const financeInfo = Enumerable.from(codesFinance).firstOrDefault(x => x?.name === code?.name);

                                const { glCode, codeTaxRate, codeRate } = this.getCodeFinanceDetails(financeInfo, invoicingType, taxRate);

                                const qty = code?.quantity ?? 1;
                                const price = new Decimal(code?.buyPrice ?? 0).toDP(4).toNumber();
                                const lineSubTotal = new Decimal(qty * price).toDP(2).toNumber();
                                const lineTax = new Decimal(lineSubTotal * codeRate).toDP(2).toNumber();
                                const lineTotal = new Decimal(lineSubTotal + lineTax).toDP(2).toNumber();

                                const desc = this.descriptionForLine(code, request?.appendLongDescription);
                                return {
                                    name: code.name,
                                    description: desc,
                                    notes: code.note,
                                    location: code.location,
                                    codeStatus: code.status,
                                    quantity: qty,
                                    price: price,
                                    subTotal: lineSubTotal,
                                    tax: lineTax,
                                    total: lineTotal,
                                    uom: code.uom,
                                    codeUoM: code.codeUoM,
                                    itemLink: {
                                        _id: workOrder._id,
                                        name: workOrder.name,
                                        lineId: code._id
                                    },
                                    glCode,
                                    taxRate: codeTaxRate,
                                    type: code?.type,
                                } as InvoiceLine;


                            }).toArray();

                        break;
                    }

                    case InvoiceCreatedType.FromPurchaseOrder: {

                        const { billTo } = customer;

                        contact = customer ?
                            {
                                _id: billTo?.company?._id,
                                name: billTo?.company?.legalName || billTo?.company?.name || customer?.name,
                                friendlyName: billTo?.company?.name,
                                address: billTo?.site?.address,
                                email: billTo?.contact?.email,
                                phone: billTo?.contact?.mobile,
                                contactId: billTo?.contact?._id,
                                contactName: billTo?.contact?.name,
                                originalAccount: {
                                    _id: customer?._id,
                                    name: customer?.name
                                },
                                type: 'Customer',
                                invoiceTheme: site?.invoiceTheme ?? customer?.invoiceTheme ?? null
                            } as InvoiceContact
                            : null;

                        links = [
                            ...links,
                            {
                                _id: purchaseOrder._id,
                                name: purchaseOrder.poNumber,
                                type: ActivityEntityName.PurchaseOrder,
                                relationship: LinkRelationship.From
                            } as EntityLink
                        ];

                        const jobLink = purchaseOrder?.links?.find(x => x.type === ActivityEntityName.Job);
                        if (jobLink) {
                            links.push({
                                _id: jobLink._id,
                                name: jobLink.name,
                                type: ActivityEntityName.Job,
                                relationship: LinkRelationship.From
                            } as EntityLink);
                            jobEntity ??= { _id: jobLink._id, name: jobLink.name };
                        }

                        lines = Enumerable
                            .from(purchaseOrder?.lines)
                            .where(x => x.createFromCode)
                            .select(line => {

                                const financeInfo = Enumerable.from(codesFinance).firstOrDefault(x => x?.name === line?.name);
                                const { glCode } = this.getCodeFinanceDetails(financeInfo, invoicingType, taxRate);

                                return {
                                    _id: line._id,
                                    name: line.name,
                                    description: line.description,
                                    notes: line.notes,
                                    location: line.location,
                                    codeStatus: CodeStatus.Normal,
                                    quantity: line.quantity,
                                    price: line.price,
                                    subTotal: line.subTotal,
                                    tax: line.tax,
                                    total: line.total,
                                    uom: line.uom,
                                    codeUoM: line.codeUoM,
                                    itemLink: line.itemLink,
                                    glCode,
                                    taxRate: line.taxRate,
                                    type: line.type,
                                } as InvoiceLine;


                            })
                            .toArray();

                        taxInclusiveOption = purchaseOrder.taxInclusiveOption;
                        timezone = company?.address?.timezone ?? timezone;

                        break;
                    }

                    case InvoiceCreatedType.Default: {

                        lines = [];
                        break;

                    }

                }

                break;
            }

            case InvoiceType.Invoice: {

                contact = this.getInvoiceContact(customer, site);
                // const { billTo } = customer;

                // contact = customer ?
                //     {
                //         _id: billTo?.company?._id,
                //         name: billTo?.company?.legalName,
                //         friendlyName: billTo?.company?.name,
                //         address: billTo?.site?.address,
                //         email: billTo?.contact?.email,
                //         phone: billTo?.contact?.mobile,
                //         contactId: billTo?.contact?._id,
                //         contactName: billTo?.contact?.name,
                //         originalAccount: {
                //             _id: customer?._id,
                //             name: customer?.name
                //         },
                //         type: 'Supplier',
                //         invoiceTheme: customer?.invoiceTheme ?? null
                //     } as InvoiceContact
                //     : null;



                switch (createdType) {

                    case InvoiceCreatedType.FromJob: {
                        //Need to make sure that we only add the labour code once
                        let labourCodeAdded = false;
                        lines = Enumerable
                            .from(job.workOrders)
                            .select(w => {
                                let codes = [];
                                if (labourCodeAdded) {
                                    codes = w?.codes;
                                }
                                else {
                                    labourCodeAdded = true;
                                    //Add the labour code as the first code
                                    codes = labourCode ? [labourCode, ...(w?.codes ?? [])] : w?.codes;
                                }

                                return Enumerable.from(codes)
                                    .where(x => InvoiceUtility.validInvoiceCode(x))
                                    .select(code => {

                                        const itemLink = {
                                            _id: w._id,
                                            name: w.name,
                                            lineId: code._id
                                        } as ItemLinkEntity;

                                        return this.createInvoiceLine(code, codesFinance, invoicingType, taxRate, itemLink, defaultIncomeTaxRate, request.appendLongDescription);

                                    }).toArray();
                            })
                            .selectMany(x => [...x])
                            .toArray();
                        break;
                    }

                    case InvoiceCreatedType.FromWorkOrder: {

                        lines = Enumerable
                            .from(job.workOrders)
                            .where(x => x._id === workOrder._id)
                            .selectMany(w => {

                                const codes = labourCode
                                    ? [labourCode, ...(w?.codes ?? [])]
                                    : w?.codes

                                return codes;
                            })
                            .where(x => InvoiceUtility.validInvoiceCode(x))
                            .select(code => {

                                const itemLink = {
                                    _id: workOrder._id,
                                    name: workOrder.name,
                                    lineId: code._id
                                } as ItemLinkEntity;

                                return this.createInvoiceLine(code, codesFinance, invoicingType, taxRate, itemLink, defaultIncomeTaxRate, request.appendLongDescription);
                            }).toArray();

                        break;
                    }

                    case InvoiceCreatedType.Default: {

                        lines = [];

                        break;
                    }
                }

                break;
            }
        }

        let subTotal = 0;
        let tax = 0;
        let total = 0;

        Enumerable
            .from(lines)
            .forEach(l => {
                subTotal += l.subTotal;
                tax += l.tax;
                total += l.total;
            });

        const now = new Date();
        const today = DateUtility.getDateTime(now, timezone).startOf('day');

        const account = invoicingType === InvoiceType.Bill
            ? technician
            : customer;

        const dueDate = this.getDueDateFromAccount(account?.credit, today).toJSDate();

        const invoice = {
            _id: null,
            active: true,
            status: InvoiceStatus.Draft,
            invoiceDate: today.toJSDate(),
            generatedDate: now,
            dueDate,
            lines,
            contact,
            billingCompany: billingCompany,
            customerReference: job?.customerWO,
            job: jobEntity,
            workOrder: workOrderEntity,
            subTotal: new Decimal(subTotal).toDP(2).toNumber(),
            tax: new Decimal(tax).toDP(2).toNumber(),
            total: new Decimal(total).toDP(2).toNumber(),
            taxInclusiveOption,
            terms: billingCompany?.invoiceDetails,
            notes: null,
            taxRate,
            timezone,
            jobAddress,
            timesheetIds: labourCode?.timesheetIds ?? [],
            theme: invoicingType === InvoiceType.Bill
                ? contact?.billTheme
                : contact?.invoiceTheme,
            links
        } as Invoice;

        if (invoicingType === InvoiceType.Bill) {

            const bill = {
                ...invoice,
                workOrder: workOrderEntity,
                customerReference: workOrder?.workOrderNumber ?? null,
                theme: contact?.billTheme
            };

            if (createdType === InvoiceCreatedType.FromPurchaseOrder) {
                bill.customerReference = purchaseOrder?.reference ?? null;
            };

            return bill as T;
        }

        return invoice as T;
    }

    // Catalogue add-item and generated invoice lines must share this. A second copy of the template is how a line kept only the short text.
    public static descriptionForLine(code: { description?: string, longDescription?: string }, appendLongDescription?: boolean): string | undefined {

        return appendLongDescription && code?.longDescription ? `${code.description} - ${code.longDescription}`.trim() : code.description;
    }

    public static createInvoiceLine(code: EmbeddedCode, codesFinance: CodeFinance[], invoicingType: InvoiceType, taxRate: TaxRate, itemLink: ItemLinkEntity, defaultIncomeTaxRate: TaxRate, appendLongDescription: boolean = false): InvoiceLine {
        const financeInfo = codesFinance?.find(x => x.name === code.name);
        const { glCode, codeTaxRate, codeRate } = this.getCodeFinanceDetails(financeInfo, invoicingType, taxRate);

        const noCharge = code.codeInvoicingStatus === CodeInvoiceStatus.NoCharge;
        const qty = code?.quantity ?? 1;
        const price = noCharge
            ? 0
            : new Decimal(code?.sellPrice ?? 0).toDP(4).toNumber();
        const lineSubTotal = new Decimal(qty * price).toDP(2).toNumber();
        const lineTax = new Decimal(lineSubTotal * codeRate).toDP(2).toNumber();
        const lineTotal = new Decimal(lineSubTotal + lineTax).toDP(2).toNumber();
        const desc = this.descriptionForLine(code, appendLongDescription);

        const line = {
            name: code.name,
            description: desc,
            notes: code.note,
            location: code.location,
            codeStatus: code.status,
            quantity: qty,
            price: price,
            subTotal: lineSubTotal,
            tax: lineTax,
            total: lineTotal,
            uom: code.uom,
            codeUoM: code.codeUoM,
            itemLink: itemLink,
            // Carried so the line can say how it was invoiced. The schema has always declared it and
            // the item sync writes it, but the line was born without it, so the field read empty on
            // every invoice ever generated.
            codeInvoicingStatus: code.codeInvoicingStatus,
            glCode,
            taxRate: codeTaxRate,
            type: code?.type
        } as InvoiceLine;

        if (code?._id === LabourCodeId) {
            line.taxRate = defaultIncomeTaxRate;
        }


        return line;
    }

    /**
     * Terms count from the Invoice Date's calendar day in the DOCUMENT timezone, which is how invoice
     * and bill dates are stored and painted. Counting in the browser zone moved the day whenever the
     * two differed, so a 2-day term landed 1 day out and Prepaid fell before the Invoice Date (WBS-11276).
     */
    public static getDueDateForInvoiceDate(credit: CreditOptions | null | undefined, invoiceDate: Date | string | number, timezone?: string): DateTime | null {

        const zone = timezone || DateUtility.getLocalTimeZone();
        const date = invoiceDate instanceof Date
            ? DateTime.fromJSDate(invoiceDate, { zone })
            : typeof invoiceDate === 'number'
                ? DateTime.fromMillis(invoiceDate, { zone })
                : typeof invoiceDate === 'string'
                    ? DateTime.fromISO(invoiceDate, { zone })
                    : null;
        if (!date?.isValid) return null;
        return this.getDueDateFromAccount(credit, date.startOf('day'));
    }

    public static getDueDateFromAccount(credit: CreditOptions | null | undefined, relativeDate: DateTime): DateTime {

        // Prepaid accounts are always due on receipt; stored due date options only apply to Credit accounts
        if (credit?.type === CreditAccountType.Prepaid) {
            return relativeDate;
        }

        const dueDateOptions = credit?.dueDate;

        //Default behaviour is last day of next month
        const method = dueDateOptions?.type ?? DueDateType.XDayFollowingMonth;
        const days = dueDateOptions?.value ?? 31;
        let dueDate = relativeDate;
        switch (method) {
            case DueDateType.DueOnReceipt:
                {
                    dueDate = relativeDate;
                    break;
                }
            case DueDateType.XDayAfterEndOfBillInvoiceMonth:
                {
                    dueDate = dueDate.endOf('month').plus({ day: days }).startOf('day'); //.toJSDate();
                    break;
                }
            case DueDateType.XDayAfterTheBillInvoiceDate:
                {
                    dueDate = dueDate.plus({ day: days }); //.toJSDate();
                    break;
                }
            case DueDateType.XDayCurrentMonth:
                {
                    const month = dueDate.month;
                    const year = dueDate.year;
                    const currentDay = dueDate.day;
                    const safeDay = DateUtility.getValidDayForMonthAndYear(days, month, year);
                    //This mode could result in a due date prior to invoice date - prevent that
                    dueDate = dueDate.set({ day: safeDay > currentDay ? safeDay : currentDay });
                    break;
                }
            case DueDateType.XDayFollowingMonth:
                {
                    dueDate = dueDate.endOf('month').plus({ day: 1 }).startOf('day');
                    const month = dueDate.month;
                    const year = dueDate.year;
                    const safeDay = DateUtility.getValidDayForMonthAndYear(days, month, year);
                    dueDate = dueDate.set({ day: safeDay });
                    break;
                }
            default:
                {
                    dueDate = dueDate.endOf('month').plus({ month: 1 }).startOf('day');
                    break;
                }
        }
        return dueDate;
    }

    public static getDefaultLine(): InvoiceLine {
        return {
            name: null,
            description: null,
            quantity: 1,
            notes: null,
            price: 0,
            tax: 0,
            subTotal: 0,
            total: 0,
            codeUoM: null,
            glCode: null,
            taxRate: null,
        } as InvoiceLine;
    }

    public static getInvoiceSummary(invoice: Invoice | Bill): InvoicingSummary {

        let invoiceSummary: InvoicingSummary = {
            _id: invoice._id,
            type: invoice.type,
            address: invoice?.jobAddress ?? invoice.contact?.address,
            jobAddress: invoice?.jobAddress,
            status: invoice.status,
            contact: invoice.contact?.name,
            invoiceNumber: invoice.invoiceNumber,
            invoiceDate: invoice.invoiceDate,
            dueDate: invoice.dueDate,
            total: invoice.total,
            subTotal: invoice.subTotal,
            version: invoice.version,
            job: invoice?.job?.name,
            integration: {
                success: invoice?.integration?.success,
                type: invoice?.integration?.type,
                integrationInvoiceId: invoice?.integration?.integrationInvoiceId
            },
            company: undefined,
            billingCompany: undefined,
            prepaymentSubTotal: invoice.prepaymentSubTotal,
            prepaymentTotal: invoice.prepaymentTotal,
            creditAmount: invoice.creditAmount,
            amountPaid: invoice.amountPaid,
        };

        return invoiceSummary;
    }

    private static getCodeFinanceDetails(financeInfo: CodeFinance, invType: InvoiceType, defaultTaxRate: TaxRate) {

        let glCode: string = null;
        let codeTaxRate: TaxRate = null;
        let codeRate: number = 0;

        if (invType == InvoiceType.Bill) {
            glCode = financeInfo?.itemPurchased ? financeInfo?.purchaseGlCode : null;
            codeTaxRate = (financeInfo?.itemPurchased ? financeInfo?.purchaseTaxRate : null) ?? defaultTaxRate;
        }
        else if (invType == InvoiceType.Invoice) {
            glCode = financeInfo?.itemSold ? financeInfo?.saleGlCode : null;
            codeTaxRate = (financeInfo?.itemSold ? financeInfo?.saleTaxRate : null) ?? defaultTaxRate;
        }
        //If we have an empty string for the glCode, force it to be null
        if (!glCode)
            glCode = null;
        codeRate = (codeTaxRate?.rate ?? 0) / 100;
        return {
            glCode,
            codeTaxRate,
            codeRate
        };
    }

    public static getDefaultInvoicing(): any {

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const today = DateUtility.getDateTime(new Date(), timezone).startOf('day');

        const defaultData: Partial<Invoice | Bill> = {
            _id: null,
            invoiceNumber: null,
            invoiceDate: today.toJSDate(),
            dueDate: today.endOf('month').plus({ day: 1 }).endOf('month').toJSDate(),
            contact: null,
            customerReference: null,
            notes: null,
            integration: null,
            job: null,
            workOrder: null,
            taxInclusiveOption: TaxInclusiveOption.Exclusive,
            billingCompany: null,
            status: InvoiceStatus.Draft,
            createdBy: null,
            taxRate: null,
            attachments: [],
            terms: null,
            timezone: timezone,
            lines: [],
            subTotal: 0,
            tax: 0,
            total: 0,

            amountPaid: 0,
            lastPaymentDate: null,
            isReconcile: false,
            roundingAmount: 0,
        };
        return defaultData;
    }

    public static getDefaultViews(): InvoiceViewSummary[] {

        const defaultViews = [];

        defaultViews.push({
            _id: InvoicingViewByDays.Last30Days,
            name: InvoicingViewByDays.Last30Days,
            isDefault: true,
            system: true,
            selected: false,
            ownerId: null,
            filters: {
                dayRange: InvoicingViewByDays.Last30Days
            }
        } as InvoiceViewSummary);

        defaultViews.push({
            _id: InvoicingViewByDays.Last60Days,
            name: InvoicingViewByDays.Last60Days,
            isDefault: false,
            system: true,
            selected: false,
            ownerId: null,
            filters: {
                dayRange: InvoicingViewByDays.Last60Days
            }
        } as InvoiceViewSummary);

        defaultViews.push({
            _id: InvoicingViewByDays.Last90Days,
            name: InvoicingViewByDays.Last90Days,
            isDefault: false,
            system: true,
            selected: false,
            ownerId: null,
            filters: {
                dayRange: InvoicingViewByDays.Last90Days
            }
        } as InvoiceViewSummary);

        defaultViews.push({
            _id: InvoicingViewByDays.Last90PlusDays,
            name: InvoicingViewByDays.Last90PlusDays,
            isDefault: false,
            system: true,
            selected: false,
            ownerId: null,
            filters: {
                dayRange: InvoicingViewByDays.Last90PlusDays
            }
        } as InvoiceViewSummary);

        return defaultViews;
    }

    public static formatLinesPrice(lines: InvoiceLine[] = []): InvoiceLine[] {

        lines = Enumerable.from(lines).select(x => {
            const price = new Decimal(x?.price ?? 0).toDP(4).toNumber();
            x.price = Math.abs(price);

            return x;
        }).toArray()

        return lines;

    }

    public static calculatePaymentAmount(total = 0, amountPaid = 0, isReconcile = false): { outstandingAmount: number, roundingAmount: number } {
        let outstandingAmount = 0;
        let roundingAmount = 0;
        total = total || 0;
        amountPaid = amountPaid || 0;
        const diffAmount = new Decimal(total - amountPaid).toDP(2).toNumber();

        if (isReconcile) {
            roundingAmount = diffAmount;
            outstandingAmount = 0;
        } else {
            roundingAmount = 0;
            outstandingAmount = diffAmount;
        }

        return { outstandingAmount, roundingAmount };
    }


    public static getInvoiceLineTotal(lines: InvoiceLine[], defaultTaxRate: TaxRate, taxInclusiveOption: TaxInclusiveOption, isSkipLineL = (line: InvoiceLine) => false): LineAccounting {


        let subTotal = new Decimal(0);
        let tax = new Decimal(0);
        let total = new Decimal(0);

        Enumerable.from(lines)
            .where(line => !isSkipLineL(line))
            .forEach(line => {

                // A consolidated line sums one job's lines at their own rates, so it is stored with a tax
                // amount and no rate. No single rate reproduces that tax: keep it rather than re-taxing
                // the line at the document rate (WBS-12170).
                if (this.isConsolidatedLine(line)) {
                    const storedSubTotal = new Decimal(line.subTotal ?? 0).toDP(2).toNumber();
                    const storedTax = new Decimal(line.tax ?? 0).toDP(2).toNumber();
                    line.subTotal = storedSubTotal;
                    line.tax = storedTax;
                    line.total = taxInclusiveOption === TaxInclusiveOption.Exclusive
                        ? new Decimal(storedSubTotal + storedTax).toDP(2).toNumber()
                        : storedSubTotal;
                    subTotal = subTotal.plus(line.subTotal);
                    tax = tax.plus(line.tax);
                    total = total.plus(line.total);
                    return;
                }

                const rate = this.getTaxRate(line.taxRate, defaultTaxRate);

                const qty = line.quantity ?? 1;

                const price = new Decimal(line?.price ?? 0).toDP(4).toNumber();

                const gross = new Decimal(qty * price).toDP(2);

                // Credit note lines carry a negative quantity, so gross can be negative. Work the
                // discount against the magnitude and restore the sign afterwards: a discount always
                // shrinks the line towards zero, it never flips its sign or inflates a credit.
                const grossSign = gross.isNegative() ? -1 : 1;
                const grossMagnitude = gross.abs();
                const discountRaw = new Decimal(line?.discount ?? 0).abs();
                const discountMagnitude = line?.discountType === 'Percent'
                    ? grossMagnitude.mul(discountRaw).div(100).toDP(2)
                    : discountRaw.toDP(2);
                // Discount reduces the taxable base; clamped at zero so it can never exceed the line.
                const lineSubTotal = Decimal.max(grossMagnitude.minus(discountMagnitude), 0).mul(grossSign).toDP(2).toNumber();

                let lineTax = 0;
                let lineTotal = 0;

                switch (taxInclusiveOption) {
                    case TaxInclusiveOption.Exclusive:
                        lineTax = new Decimal(Number(lineSubTotal * rate)).toDP(2).toNumber();
                        lineTotal = new Decimal(lineSubTotal + lineTax).toDP(2).toNumber();
                        break;

                    case TaxInclusiveOption.Inclusive:
                        lineTax = inclusiveLineTax(lineSubTotal, rate);
                        lineTotal = lineSubTotal;
                        break;

                    case TaxInclusiveOption.NoTax:
                    default:
                        lineTax = 0;
                        lineTotal = lineSubTotal;
                        break;
                }

                line.subTotal = lineSubTotal;
                line.tax = lineTax;
                line.total = lineTotal;

                subTotal = subTotal.plus(line.subTotal);
                tax = tax.plus(line.tax);
                total = total.plus(line.total);
            });

        return {
            lines: lines,
            subTotal: subTotal.toDP(4).toNumber(),
            tax: tax.toDP(4).toNumber() ?? 0,
            total: total.toDP(4).toNumber() ?? 0
        };
    }

    ///Need to review the rounding issues when working backwards from the tax inclusive option
    //Theres a difference where we are using this to calculate the ex-tax unit price
    public static calculateTax(subTotal: number, taxRate: TaxRate, taxInclusiveOption: TaxInclusiveOption, defaultTaxRate: TaxRate) {
        const rate = this.getTaxRate(taxRate, defaultTaxRate);
        let lineTax = 0;
        switch (taxInclusiveOption) {
            case TaxInclusiveOption.Exclusive:
                lineTax = new Decimal(Number(subTotal * rate)).toDP(2).toNumber();
                break;

            case TaxInclusiveOption.Inclusive:
                lineTax = inclusiveLineTax(subTotal, rate);
                break;

            case TaxInclusiveOption.NoTax:
            default:
                lineTax = 0;
                break;
        }
        return lineTax;
    }

    public static calculateExTaxUnitPrice(unitPrice: number, taxRate: TaxRate, taxInclusiveOption: TaxInclusiveOption, defaultTaxRate: TaxRate) {
        const rate = this.getTaxRate(taxRate, defaultTaxRate);
        let exTaxUnitPrice = 0;
        switch (taxInclusiveOption) {
            case TaxInclusiveOption.Exclusive:
            case TaxInclusiveOption.NoTax:
                exTaxUnitPrice = unitPrice;
                break;
            case TaxInclusiveOption.Inclusive:
                const taxPlus = 1 + rate;
                const lineTax = new Decimal((unitPrice / taxPlus) * rate).toDP(4).toNumber();
                exTaxUnitPrice = unitPrice - lineTax;
                break;
        }
        return exTaxUnitPrice;
    }

    public static getPurchaseOrderLineTotal(lines: PurchaseOrderLine[], defaultTaxRate: TaxRate, taxInclusiveOption: TaxInclusiveOption, markupPercent?: number): LineAccounting {


        const markupRate = 0; // markupPercent ? markupPercent / 100 : 0;

        let tax = 0;
        let subTotal = 0;
        let total = 0;
        let supplierMarkup = 0;

        Enumerable.from(lines)
            .forEach(line => {
                const rate = this.getTaxRate(line.taxRate, defaultTaxRate);

                const qty = line.quantity ?? 1;

                const price = new Decimal(+line?.price).toDP(4).toNumber();

                const lineSubTotal = new Decimal(qty * price).toDP(2).toNumber();;

                let lineTax = 0;
                let lineTotal = 0;
                let lineMarkup = 0;

                switch (taxInclusiveOption) {
                    case TaxInclusiveOption.Exclusive:
                        lineTax = Number(lineSubTotal * rate);
                        lineTotal = lineSubTotal + lineTax;
                        break;

                    case TaxInclusiveOption.Inclusive:
                        const taxPlus = 1 + rate;
                        lineTax = (lineSubTotal / taxPlus) * rate;
                        lineTotal = lineSubTotal;
                        break;
                }

                lineMarkup = Number(lineSubTotal * markupRate);
                // add code
                line.subTotal = lineSubTotal;
                line.tax = lineTax;
                line.total = lineTotal;

                supplierMarkup += lineMarkup;
                subTotal += lineSubTotal;
                tax += lineTax;
                total += lineTotal + lineMarkup;
            });


        return {
            lines: lines,
            supplierMarkup: supplierMarkup ?? 0,
            subTotal: subTotal ?? 0,
            tax: tax ?? 0,
            total: total ?? 0
        };
    }

    public static sortedInvoiceActivities(activities: Activity[]): Activity[] {

        const sorted = Enumerable.from(activities)
            .orderByDescending(x => x.createdDate)
            .thenBy(x => {

                switch (x.type) {
                    case ActivityType.JobBilled:
                        return 6;
                    case ActivityType.InvoiceCreated:
                        return 5;
                    case ActivityType.InvoiceApproved:
                        return 4;
                    case ActivityType.InvoiceReissue:
                        return 3;
                    case ActivityType.InvoiceError:
                        return 2;
                    case ActivityType.InvoiceVoid:
                        return 1;
                }

            }).toArray();

        return sorted;

    }

    public static sortedBillActivities(activities: Activity[]): Activity[] {

        const sorted = Enumerable.from(activities)
            .orderBy(x => x.createdDate)
            .thenBy(x => {

                switch (x.type) {
                    case ActivityType.WorkOrderBill:
                        return 6;
                    case ActivityType.BillCreated:
                        return 5;
                    case ActivityType.BillApproved:
                        return 4;
                    case ActivityType.BillReissue:
                        return 3;
                    case ActivityType.BillError:
                        return 2;
                    case ActivityType.BillVoid:
                        return 1;
                }

            }).toArray();

        return sorted;

    }

    public static getCreditAmount(invoices: Invoice[]): CreditAmount {

        if (!invoices?.length) {
            return {
                outstanding: 0,
                overdue: 0
            } as CreditAmount;
        }

        let total = 0;
        let payments = 0;
        let rounding = 0;
        let overdueTotal = 0;
        let overduePayments = 0;
        let overdueRounding = 0;



        Enumerable
            .from(invoices)
            .forEach(x => {
                total += x?.total ?? 0;
                payments += x?.amountPaid ?? 0;
                rounding += x?.roundingAmount ?? 0;

                const timezone = x?.jobAddress?.timezone ?? x?.timezone;
                const dueDate = DateUtility.getDateTime(x.dueDate, timezone);
                const today = DateUtility.getDateTime(new Date(), timezone).startOf('day');

                if (dueDate < today) {
                    overdueTotal += x?.total ?? 0;
                    overduePayments += x?.amountPaid ?? 0;
                    overdueRounding += x?.roundingAmount ?? 0;
                }
            });

        return {
            outstanding: new Decimal((total - (payments + rounding))).toDP(2).toNumber(),
            overdue: new Decimal((overdueTotal - (overduePayments + overdueRounding))).toDP(2).toNumber()
        } as CreditAmount;

    }

    // UI overdue-badge rule: invoice still owes money (outstandingAmount > 0) AND is due before the
    // start of today in its timezone; credit notes & prepayments never qualify. Shares ONLY the
    // due-before-today cutoff with getCreditAmount — that aggregate ignores outstanding amount and type.
    public static isOverdue(invoice: Invoice): boolean {

        if (!invoice?.dueDate || (invoice.outstandingAmount ?? 0) <= 0) return false;

        const excludedTypes = [InvoiceType.CreditNote, InvoiceType.CreditNoteSupplier, InvoiceType.PrepaymentInvoice, InvoiceType.PrepaymentSupplier];
        if (excludedTypes.includes(invoice.type)) return false;

        const timezone = invoice.jobAddress?.timezone ?? invoice.timezone;
        const dueDate = DateUtility.getDateTime(invoice.dueDate, timezone);
        const today = DateUtility.getDateTime(new Date(), timezone).startOf('day');

        return dueDate < today;
    }

    private static validInvoiceCode(code: EmbeddedCode, options?: { bypassInvoiceCheck: boolean }): boolean {

        if (code?.status === CodeStatus.Cancelled)
            return false;

        if (code?.codeInvoicingStatus === CodeInvoiceStatus.NotBillable)
            return false;

        if (!options?.bypassInvoiceCheck && code?.invoice)
            return false;

        //Do not add a labour code with a zero total
        if (code.type === "LABOUR" && code.sellPrice == 0)
            return false;

        return true;
    }


    public static validBillCode(code: EmbeddedCode, suppliedByContractor: boolean = true): boolean {

        if (code?.status === CodeStatus.Cancelled)
            return false;

        // A Not Payable line carries a zeroed buy price, so billing it raised a $0 line — and a wholly
        // Not Payable stage auto-generated a $0 bill on finalise.
        if (code?.payable === false)
            return false;

        if (suppliedByContractor && !code?.suppliedBySubcontractor)
            return false;

        if (!suppliedByContractor && code?.suppliedBySubcontractor)
            return false;

        if (code.bill)
            return false;

        if (!!code?.purchaseOrder?._id)
            return false;

        return true;

    }

    /** A server-built job summary line on a consolidated invoice or bill: it carries the summed lines' links and no rate. */
    public static isConsolidatedLine(line: Pick<InvoiceLine, 'taxRate' | 'links'>): boolean {

        return !line?.taxRate && !!line?.links?.length;
    }

    public static getTaxRate(lineTax: TaxRate, defaultTax: TaxRate): number {
        const rate = lineTax?.rate ?? defaultTax?.rate ?? 0;
        return rate / 100;
    }


    public static updateInvoices(invoice: Invoice, items: InvoicingSummary[]): InvoicingSummary[] {

        const index = Enumerable.from(items).indexOf((x) => x._id === invoice._id);

        const newItem = this.getInvoiceSummary(invoice);
        const item: any = {
            ...newItem,
            //  contact: invoice.contact.friendlyName,
            contactId: invoice.contact._id,
            job: invoice.job?.name,
            workOrder: invoice.workOrder?.name
        }

        const newItems = index === -1 ? [...items, item] : [...items.slice(0, index), item, ...items.slice(index + 1)];
        return newItems;
    }

    public static getFinanceToolTip(item: EmbeddedCodeGroup): string {

        if (item?.invoiceApproved && item?.billApproved) {
            return `Invoice/Bill ${item.invoice.name}/${item.bill.name} is linked to this item. Editing is locked.`;
        }

        if (item?.invoiceApproved) {
            return `Invoice ${item.invoice.name} is linked to this item. Editing is locked.`;
        }

        if (item?.billApproved) {
            return `Bill ${item.bill.name} is linked to this item. Editing is locked.`;
        }

        return "";
    }

    public static getTimesheetFinanceToolTip(item: TimesheetTable): string {

        if (item?.invoiceApproved) {
            return `Invoice ${item.invoice.name} is linked to this item. Editing is locked.`;
        }

        return "";
    }

    public static getFinanceRecordRights<T extends Bill | Invoice>(record: T, canManageRecord: boolean, canManageApprovedRecord: boolean, canAccessPayment: boolean, canManagePayment: boolean): FinanceRecordRights {

        const draft = record?.status === InvoiceStatus.Draft;
        const approved = record?.status === InvoiceStatus.Approved;
        const typePrepayment = [InvoiceType.PrepaymentInvoice, InvoiceType.PrepaymentSupplier].includes(record?.type);
        const typeCreditNote = [InvoiceType.CreditNote, InvoiceType.CreditNoteSupplier].includes(record?.type) || record?.total < 0;
        const typeBill = [InvoiceType.Bill].includes(record?.type);

        const noPayments = !record?.hasPayments
        const noAllocation = !record?.hasAllocation

        const showButtonSync = canAccessPayment && canManagePayment && approved && !typePrepayment; // TODO: maybe enable sync prepayment invoice later, show when paid amount == prepayment total
        const showButtonEdit = ((canManageRecord && draft) || (canManageRecord && approved && canManageApprovedRecord && noPayments && noAllocation))
            && !typePrepayment; // TODO: enable edit prepayment invoice, can change the lines but the prepayment total should be the same
        const showButtonProcessing = canManageRecord && noPayments;
        const showButtonReissue = canManageRecord && approved && !typePrepayment;
        const showTabPayment = canAccessPayment && !draft;

        const showUpdateAndRecalculate = showButtonEdit && !typeCreditNote;
        const showEditForPrepayment = typePrepayment && noPayments && noAllocation;

        const showButtonCopyToCreditNote = canManageRecord && canManageApprovedRecord && approved && !typePrepayment && !typeCreditNote && !typeBill;


        const rights: FinanceRecordRights = {
            showButtonSync,
            showButtonEdit,
            showButtonProcessing,
            showButtonReissue,
            showTabPayment,
            showUpdateAndRecalculate,
            showEditForPrepayment: showEditForPrepayment,
            showButtonCopyToCreditNote,
        };

        return rights;
    }

    public static canEditCustomer(invoice: Invoice, canManageApproved: boolean) {
        const domain = InvoiceCalculationDomain.from(invoice, false);
        const isDraft = invoice.status === InvoiceStatus.Draft;
        const isApproved = invoice.status === InvoiceStatus.Approved;
        const isValidType = domain.isType(InvoiceType.Invoice) || domain.isType(InvoiceType.CreditNote);

        return isValidType && (isDraft || (isApproved && canManageApproved));
    }

    /**
     * Server-owned Bill totals. EF V2 strips client `subTotal`/`tax`/`total` as protected fields, so
     * every Bill type is computed here; without it a normal Bill reaches validation with no total and
     * is refused as negative (WBS-11531). Credit types keep their sign flip.
     */
    public static recalculateBill(bill: Bill): Bill {
        if (!bill) {
            return bill;
        }

        if (bill.type === InvoiceType.CreditNote || bill.type === InvoiceType.CreditNoteSupplier) {
            return InvoiceUtility.recalculateCreditNote(bill, true) as Bill;
        }

        if (bill.type === InvoiceType.PrepaymentSupplier) {
            return InvoiceUtility.recalculatePrepayment(bill);
        }

        const totalModel = InvoiceUtility.getInvoiceLineTotal(bill.lines ?? [], bill.taxRate, bill.taxInclusiveOption);
        const calculated = InvoiceCalculationDomain.from({ ...bill, ...totalModel }, true).calculateAmounts();

        return { ...bill, ...totalModel, ...calculated } as Bill;
    }

    /**
     * Server-owned invoice totals. EF V2 strips client subTotal, tax, total, and outstandingAmount,
     * so an edit must be computed here or the stored totals stay at the previous save (WBS-12104).
     * Credit notes keep their sign flip.
     */
    public static recalculateInvoice(invoice: Invoice): Invoice {
        if (!invoice) {
            return invoice;
        }

        if (invoice.type === InvoiceType.CreditNote || invoice.type === InvoiceType.CreditNoteSupplier) {
            return InvoiceUtility.recalculateCreditNote(invoice, false) as Invoice;
        }

        if (invoice.type === InvoiceType.PrepaymentInvoice) {
            return InvoiceUtility.recalculatePrepayment(invoice);
        }

        const totalModel = InvoiceUtility.getInvoiceLineTotal(invoice.lines ?? [], invoice.taxRate, invoice.taxInclusiveOption);
        const calculated = InvoiceCalculationDomain.from({ ...invoice, ...totalModel }, false).calculateAmounts();

        return { ...invoice, ...totalModel, ...calculated } as Invoice;
    }

    /**
     * Server-owned prepayment totals, stored as legacy does: the amount in prepaymentSubTotal / Tax / Total
     * and Sub Total / Tax / Total at 0. Legacy lists, Receive Payment and allocations read only the
     * prepayment fields, so totals stored as a normal invoice show $0.00 there (WBS-12378). Paid and
     * outstanding are kept: a paid prepayment holds its remaining credit as negative outstanding. A new
     * one stores them as 0, as legacy does — allocations subtract amountPaid, and a missing field is null.
     */
    public static recalculatePrepayment<T extends Invoice | Bill>(doc: T): T {

        const totals = InvoiceUtility.getInvoiceLineTotal(doc.lines ?? [], doc.taxRate, doc.taxInclusiveOption);
        return {
            ...doc,
            subTotal: 0,
            tax: 0,
            total: 0,
            prepaymentSubTotal: totals.subTotal,
            prepaymentTax: totals.tax,
            prepaymentTotal: totals.total,
            amountPaid: doc.amountPaid ?? 0,
            outstandingAmount: doc.outstandingAmount ?? 0,
        } as T;
    }

    public static recalculateCreditNote(item: Invoice | Bill, isBill: boolean) {
        if (!item) {
            return item;
        }

        if (item.type === InvoiceType.CreditNote || item.type === InvoiceType.CreditNoteSupplier) {
            for (const line of item.lines) {

                if (line.price < 0) {
                    line.price = Math.abs(line.price);
                }

                if (line.quantity > 0) {
                    line.quantity = line.quantity * -1;
                }
                else {
                    line.quantity = Math.abs(line.quantity);
                }
            }

            const totalModel = InvoiceUtility.getInvoiceLineTotal(item.lines, item.taxRate, item.taxInclusiveOption);
            const domain = InvoiceCalculationDomain.from({ ...item, ...totalModel }, isBill);

            const calculated = domain.calculateAmounts();
            item = {
                ...item,
                ...totalModel,
                ...calculated,
            } as any
        }

        return item;
    }

    /**
     * add un-invoiced items from job to invoice
     * update unvoiced items
     * update invoice total, subTotal, tax
     * update invoice billing company
     * update labour line
     */
    public static updateAndRecalculateInvoice(options: {
        invoice: Invoice,
        job: Job,
        company: Company,
        codesFinance: CodeFinance[],
        labourCode: EmbeddedCode,
        appendLongDescription?: boolean
    }): Invoice {

        const { invoice, job, labourCode, company, codesFinance, appendLongDescription } = options;

        const stageId = invoice.workOrder?._id;
        const jobId = invoice.job?._id;
        const stage = job?.workOrders?.find(x => x._id === stageId);

        if (!jobId)
            return invoice;


        const laborLink: ItemLinkEntity = stage
            ? {
                _id: stage._id,
                name: stage.name,
                lineId: labourCode?._id
            }
            : {
                _id: jobId,
                name: job.name,
                lineId: labourCode?._id
            };

        const laborLine = labourCode
            ? this.createInvoiceLine(labourCode, codesFinance, InvoiceType.Invoice, invoice.taxRate, laborLink, invoice.taxRate, appendLongDescription)
            : null;

        const codeMap = new Map<string, EmbeddedCode>();

        const newLines = Enumerable
            .from(job.workOrders)
            .where(x => {
                return stageId
                    ? x._id === invoice.workOrder._id
                    : true;
            })
            .select(stage => {

                return Enumerable
                    .from(stage.codes)
                    .doAction(x => {
                        if (x.invoice?._id === invoice._id)
                            codeMap.set(x._id, x);
                    })
                    .where(x => InvoiceUtility.validInvoiceCode(x))
                    .select(code => {
                        const itemLink: ItemLinkEntity = {
                            _id: stage._id,
                            name: stage.name,
                            lineId: code._id
                        };

                        const invoiceLine = this.createInvoiceLine(code, codesFinance, InvoiceType.Invoice, invoice.taxRate, itemLink, invoice.taxRate, appendLongDescription);
                        return invoiceLine;
                    })
                    .toArray();
            })
            .selectMany(x => x)
            .toArray();

        const oldLine = Enumerable
            .from(invoice.lines)
            // Labour is rebuilt separately as laborLine — exclude it by its stable id, not `type`.
            // Field-App-created invoices leave the labour line's `type` null, so a `type !== 'LABOUR'` check alone lets it through.
            .where(x => x.type !== 'LABOUR' && x.itemLink?.lineId !== LabourCodeId)
            .select(x => {
                if (!x.itemLink) {
                    return x;
                }

                const code = codeMap.get(x.itemLink.lineId);

                // Line's code is no longer on the job (or is a synthetic line) — drop it rather than crash.
                if (!code)
                    return null;

                if (code.status === CodeStatus.Cancelled)
                    return null;

                if (code.codeInvoicingStatus === CodeInvoiceStatus.NotBillable)
                    return null;

                const invoiceLine = this.createInvoiceLine(code, codesFinance, InvoiceType.Invoice, invoice.taxRate, x.itemLink, invoice.taxRate, appendLongDescription);
                return invoiceLine;
            })
            .where(x => !!x)
            .toArray();


        const lines = [
            laborLine,
            ...oldLine,
            ...newLines,
        ].filter(x => !!x);

        const { total, subTotal, tax } = InvoiceUtility.getInvoiceLineTotal(lines, invoice.taxRate, invoice.taxInclusiveOption);

        const updatedInvoice: Invoice = {
            ...invoice,
            lines,
            total,
            subTotal,
            tax,
            timesheetIds: labourCode?.timesheetIds ?? invoice.timesheetIds,
        }

        if (company) {
            updatedInvoice.billingCompany = InvoiceUtility.buildInvoiceCompany(company);
        }

        return updatedInvoice;

    }

    public static buildInvoiceCompany(company: Company): InvoiceBillingCompany {

        if (!company) return null;

        return <InvoiceBillingCompany>{
            _id: company._id,
            name: company.name,
            address: company.address,
            phone: company.phone,
            email: company.email,
            taxId: company.taxId,
            invoiceDetails: company.invoiceDetails
        };
    }

    // The Field App builds the labour invoice line without a `type`; backfill it from the stable LabourCodeId
    // so downstream labour handling (recalculate, timesheet sync) can identify the line by `type === 'LABOUR'`.
    public static fillLabourLineType(invoice: Invoice): void {

        const labourLine = invoice?.lines?.find(line => line.itemLink?.lineId === LabourCodeId);
        if (labourLine && labourLine.type !== 'LABOUR') {
            labourLine.type = 'LABOUR';
        }
    }

    public static mapInvoiceTypeToActivityName(type: InvoiceType): ActivityEntityName {
        switch (type) {
            case InvoiceType.CreditNote:
                return ActivityEntityName.CreditNote;

            case InvoiceType.PrepaymentInvoice:
                return ActivityEntityName.PrepaymentInvoice;

            case InvoiceType.PrepaymentSupplier:
                return ActivityEntityName.PrepaymentSupplier;

            case InvoiceType.CreditNoteSupplier:
                return ActivityEntityName.CreditNoteSupplier;

            case InvoiceType.Bill:
                return ActivityEntityName.Bill;

            default:
                return ActivityEntityName.Invoice;

        }
    }
}

export interface FinanceRecordRights {
    showButtonSync: boolean;
    showButtonEdit: boolean;
    showButtonProcessing: boolean;
    showButtonReissue: boolean;
    showTabPayment: boolean;
    showUpdateAndRecalculate: boolean;
    showEditForPrepayment: boolean;
    showButtonCopyToCreditNote: boolean;
}
